import axios from 'axios';
import React, { useRef, useEffect, useState, useCallback } from 'react';
import Carousels from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import PxMainPage from './PxMainPage';
import { Spinner } from 'react-bootstrap';
import NewProductCardItem from "../Components/NewProductCardItem";
import '../Components/CardsContainer.css';
import '../Components/NewProductCardItem.css';
import './HomeNew.css';
import { Link, NavLink } from "react-router-dom";
import { useDispatch } from 'react-redux';
import { setProducts } from '../redux/actions';
import Footer from '../Components/Footer';
import { useTranslation } from 'react-i18next';

// ─── Carousel breakpoints ─────────────────────────────────────────────────────
const responsive = {
  desktopLarge: { breakpoint: { max: 3000, min: 1400 }, items: 4, slidesToSlide: 4 },
  desktop:      { breakpoint: { max: 1400, min: 800  }, items: 3, slidesToSlide: 3 },
  tablet:       { breakpoint: { max: 800,  min: 540  }, items: 2, slidesToSlide: 2 },
  mobile:       { breakpoint: { max: 540,  min: 280  }, items: 2, slidesToSlide: 2 },
  minmobile:    { breakpoint: { max: 280,  min: 0    }, items: 1, slidesToSlide: 1 },
};



// ─── Hero video carousel ──────────────────────────────────────────────────────
const HERO_VIDEOS = [
  {
     src:'https://hdbrandblob.blob.core.windows.net/storage/video_3.webm',
    poster: require('../assets/bunner2.webp') ,
  },
   { 
     src: 'https://hdbrandblob.blob.core.windows.net/storage/IMG_0103.webm',
    poster: require('../assets/bunner1.jpg'),
  },
{ 
    src:'https://hdbrandblob.blob.core.windows.net/storage/main3.webm',
    poster: require('../assets/bunner31.jpg'),
  }

];

// ─── Instagram Reel ───────────────────────────────────────────────────────────
const INSTAGRAM_URL = 'https://www.instagram.com/hd_ua_';
const REEL_SRC = require('../assets/reel1.webm'); 

const InstagramReels = () => {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) v.play().catch(() => {});
      else v.pause();
    }, { threshold: 0.25 });
    obs.observe(v);
    return () => obs.disconnect();
  }, []);
  return (
    <section className="home-reels">
      <div className="home-reels__inner">
        <div className="home-reels__card">
          <div className="home-reels__handle">
            <img className="home-reels__handle-logo" src={require('../assets/hendl.png')} alt="HD" />
            <span className="home-reels__handle-name">hd_ua_</span>
          </div>
          <video
            ref={videoRef}
            className="home-reels__img"
            src={REEL_SRC}
            muted
            loop
            playsInline
            preload="metadata"
          />
          <div className="home-reels__bottom">
            <h1 className="home-reels__label">REELS</h1>
            <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="home-reels__btn">
              {t('go_to_instagram')}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─── VideoCard — lazy loads & autoplays like a GIF when scrolled into view ───
const VideoCard = ({ src, poster }) => {
  const videoRef = useRef(null);
  const loaded = useRef(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (!loaded.current) {
            video.load();
            loaded.current = true;
          }
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.25, rootMargin: '80px' }
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, [src]);

  return (
    <div className="video-card">
      <video
        ref={videoRef}
        poster={poster}
        muted
        loop
        playsInline
        preload="metadata"
        className="video-card__player"
      >
        <source src={src} type="video/webm" />
      </video>
      <div className="video-card__overlay" />
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const Home = () => {
  const { i18n, t } = useTranslation();
  const [loading, setLoading]       = useState(true);
  const [contents, setContents]     = useState([]);
  const [subcategory2, setSubcategory2] = useState([]);
  const [subcategory3, setSubcategory3] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState('UAH');
  const [exchangeRates, setExchangeRates]        = useState({ usd: 1, eur: 1 });
  const [headerSolid, setHeaderSolid] = useState(false);
  const [heroIndex, setHeroIndex] = useState(0);
  const heroVideoRefs = useRef([]);
  const heroTimerRef  = useRef(null);
  const isDraggingNew  = useRef(false);
  const isDraggingSale = useRef(false);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const dispatch = useDispatch();

  // ── Currency ──────────────────────────────────────────────────────────────
  const handleCurrencyChange = (cur) => {
    setSelectedCurrency(cur);
    window.sessionStorage.setItem('selectedCurrency', cur);
    window.localStorage.setItem('selectedCurrency', cur);
  };

  const convertPrice = (price, currency) => {
    if (currency === 'USD') return (price * exchangeRates.usd).toFixed(0);
    if (currency === 'EUR') return (price * exchangeRates.eur).toFixed(0);
    return price;
  };


  // ── Routing helper ────────────────────────────────────────────────────────
  const generatePath = (categoryId) => {
    switch (categoryId) {
      case 1: return 'clothes';
      case 2: return 'shoes';
      case 3: return 'accessorise';
      default: return 'unknown';
    }
  };

  // ── Data fetch ────────────────────────────────────────────────────────────
  const fetchExchangeRates = async () => {
    try {
      const res  = await fetch('https://api.exchangerate-api.com/v4/latest/UAH');
      const data = await res.json();
      setExchangeRates({ usd: data.rates.USD, eur: data.rates.EUR });
    } catch (err) {
      console.error('Exchange rate error:', err);
    }
  };

  // ── Scroll-based header ───────────────────────────────────────────────────
  useEffect(() => {
    const onScroll = () => setHeaderSolid(window.pageYOffset > window.innerHeight * 0.75);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // ── Hero carousel auto-advance ────────────────────────────────────────────
  const startHeroTimer = useCallback(() => {
    if (HERO_VIDEOS.length <= 1) return;
    clearInterval(heroTimerRef.current);
    heroTimerRef.current = setInterval(() => {
      setHeroIndex(i => (i + 1) % HERO_VIDEOS.length);
    }, 6000);
  }, []);

  useEffect(() => {
    startHeroTimer();
    return () => clearInterval(heroTimerRef.current);
  }, [startHeroTimer]);

  const heroTouchStart = useRef(null);

  const handleHeroTouchStart = (e) => {
    heroTouchStart.current = e.touches ? e.touches[0].clientX : e.clientX;
  };
  const handleHeroTouchEnd = (e) => {
    if (heroTouchStart.current === null) return;
    const endX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
    const delta = heroTouchStart.current - endX;
    if (Math.abs(delta) > 40) {
      setHeroIndex(i => delta > 0
        ? (i + 1) % HERO_VIDEOS.length
        : (i - 1 + HERO_VIDEOS.length) % HERO_VIDEOS.length
      );
      startHeroTimer();
    }
    heroTouchStart.current = null;
  };

  // ── Play active hero video, pause others ──────────────────────────────────
  useEffect(() => {
    heroVideoRefs.current.forEach((v, i) => {
      if (!v) return;
      if (i === heroIndex) {
        v.play().catch(() => {});
      } else {
        v.pause();
         v.currentTime = 0;
      }
    });
  }, [heroIndex]);

  useEffect(() => {
    window.scrollTo(0, 0);
    window.sessionStorage.removeItem('selectedProduct');
    window.sessionStorage.removeItem('visibleItems');
    window.sessionStorage.removeItem('filters');

    axios
      .get(`${API_BASE_URL}/api/Product/GetProducts`)
      .then((res) => {
        dispatch(setProducts(res.data));
        setContents(res.data);
      })
      .catch((err) => console.error('Products fetch error:', err))
      .finally(() => setTimeout(() => setLoading(false), 500));

    axios
      .get(`${API_BASE_URL}/api/Specification/GetSubCategoryNamesByCategoryId?id=2`)
      .then((res) => setSubcategory2(res.data))
      .catch((err) => console.error('Subcategory2 fetch error:', err));

    axios
      .get(`${API_BASE_URL}/api/Specification/GetSubCategoryNamesByCategoryId?id=3`)
      .then((res) => setSubcategory3(res.data))
      .catch((err) => console.error('Subcategory3 fetch error:', err));

    fetchExchangeRates();

    const saved = window.localStorage.getItem('selectedCurrency');
    if (saved) setSelectedCurrency(saved);
  }, [dispatch]);

  const showSale = contents.filter((x) => x.isDiscount).length > 4;

  // ── JSX ───────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Fixed header */}
      <div
        className={`home-header-wrap${headerSolid ? ' home-header-wrap--solid' : ''}`}
        style={{ position: 'fixed', width: '100%', zIndex: 1000, top: 0 }}
      >
        <PxMainPage
          convertPrice={convertPrice}
          selectedCurrency={selectedCurrency}
          handleCurrencyChange={handleCurrencyChange}
        />
      </div>

      {/* ══════════════════════════════════════════════════════════════
          HERO — full-viewport, dark, video background
      ══════════════════════════════════════════════════════════════ */}
      <section
        className="home-hero"
        onTouchStart={handleHeroTouchStart}
        onTouchEnd={handleHeroTouchEnd}
        onMouseDown={handleHeroTouchStart}
        onMouseUp={handleHeroTouchEnd}
      >
        {/* Video slides */}
        {HERO_VIDEOS.map((v, i) => (
          <video
            key={i}
            ref={el => heroVideoRefs.current[i] = el}
            className={`home-hero__video${i === heroIndex ? ' home-hero__video--active' : ''}`}
            muted
            loop
            playsInline
            preload="none"
            poster={v.poster}
            src={v.src}
          />
        ))}

        {/* Dark overlay */}
        <div className="home-hero__overlay" aria-hidden="true" />

        {/* Content */}
        <div className="home-hero__content">
          {HERO_VIDEOS.length > 1 && (
            <div className="home-hero__dots">
              {HERO_VIDEOS.map((_, i) => (
                <button
                  key={i}
                  className={`home-hero__dot${i === heroIndex ? ' home-hero__dot--active' : ''}`}
                  onClick={() => { setHeroIndex(i); startHeroTimer(); }}
                  aria-label={`Slide ${i + 1}`}
                />
              ))}
            </div>
          )}
          <div className="home-hero__actions">
            <NavLink to="/shoes" className="btn-hero-primary">
              {t('buy_online')}
            </NavLink>
          </div>
        </div>
      </section>


      {/* ══════════════════════════════════════════════════════════════
          PRODUCTS — loading / new items / video reel / sale
      ══════════════════════════════════════════════════════════════ */}
      {loading ? (
        <div className="home-loading">
          <Spinner variant="secondary" animation="border" />
        </div>
      ) : (
        <>
          {/* ── New items ── */}
          <section className="home-carousel-section">
            <div className="home-section-header">
              <h2 className="home-section-title">{t('new_items')}</h2>
              <NavLink to="/shoes" className="home-section-link">
                {t('show_more')}
              </NavLink>
            </div>
            <Carousels
              responsive={responsive}
              itemClass="carousel-item-padding"
              containerClass="carousel-container"
              beforeChange={() => { isDraggingNew.current = true; }}
              afterChange={() => { setTimeout(() => { isDraggingNew.current = false; }, 100); }}
            >
              {contents.filter((x) => x.isNew && x.visible).slice(0, 10).map((x) => (
                <div
                  className="something"
                  key={x.id}
                  onClickCapture={(e) => { if (isDraggingNew.current) { e.preventDefault(); e.stopPropagation(); } }}
                >
                  <NewProductCardItem
                    link={`/${generatePath(x.categoryid)}/${x.subCategoryid}/${x.id}`}
                    id_key={x.id}
                    imageSrc1={x.image}
                    imageSrc2={x.image2}
                    imageSrc3={x.image3}
                    isNew={x.isNew}
                    isDiscount={x.isDiscount}
                    isLiked={false}
                    descriprion={i18n.language === 'en' ? x.nameEng : x.name}
                    price1={convertPrice(x.price, selectedCurrency)}
                    currency={selectedCurrency}
                    price2={convertPrice(x.salePrice, selectedCurrency)}
                  />
                </div>
              ))}
            </Carousels>
          </section>

          {/* ── Brand Story ── */}
          <section className="home-video-reel">
            <div className="home-section-header">
              <h2 className="home-section-title home-section-title--light">BESTSELLERS</h2>
            </div>
            <div className="video-reel-grid">
              <VideoCard src='https://hdbrandblob.blob.core.windows.net/storage/bestseller2.webm' />
               <VideoCard src='https://hdbrandblob.blob.core.windows.net/storage/bestseller3.webm' />
              <VideoCard src='https://hdbrandblob.blob.core.windows.net/storage/bestseller4.webm' />
              <VideoCard src='https://hdbrandblob.blob.core.windows.net/storage/bestseller1.webm' />
            </div>
          </section>

          {/* ── Sale / discount items ── */}
          {showSale && (
            <section className="home-sale-section">
              <div className="home-section-header">
                <h2 className="home-section-title">{t('sale')}</h2>
                <Link to="/sale" className="home-section-link">
                  {t('show_more')}
                </Link>
              </div>
              <Carousels
                responsive={responsive}
                itemClass="carousel-item-padding"
                containerClass="carousel-container"
                beforeChange={() => { isDraggingSale.current = true; }}
                afterChange={() => { setTimeout(() => { isDraggingSale.current = false; }, 100); }}
              >
                {contents
                  .filter((x) => x.isDiscount && x.visible)
                  .slice(-10)
                  .map((x) => (
                    <div
                      className="something"
                      key={x.id}
                      onClickCapture={(e) => { if (isDraggingSale.current) { e.preventDefault(); e.stopPropagation(); } }}
                    >
                      <NewProductCardItem
                        link={`/${generatePath(x.categoryid)}/${x.subCategoryid}/${x.id}`}
                        id_key={x.id}
                        imageSrc1={x.image}
                        imageSrc2={x.image2}
                        imageSrc3={x.image3}
                        isNew={x.isNew}
                        isDiscount={x.isDiscount}
                        isLiked={false}
                        descriprion={i18n.language === 'en' ? x.nameEng : x.name}
                        price1={convertPrice(x.price, selectedCurrency)}
                        currency={selectedCurrency}
                        price2={convertPrice(x.salePrice, selectedCurrency)}
                      />
                    </div>
                  ))}

              </Carousels>
            </section>
          )}

          {/* ── Instagram Reels ── */}
          <InstagramReels />
        </>
      )}

      <Footer />
    </div>
  );
};

export default Home;
